import React, { useEffect, useState } from 'react';
import { configureMyUplink, setMyUplinkEnabled, triggerMyUplinkPoll } from '../../services/api';
import { MyUplinkStatus } from '../../types';
import { formatInTZ } from '../../utils/timezone';
import { ui } from '../../styles/shared';

interface Props {
    status: MyUplinkStatus | null;
    loading: boolean;
    timezone: string;
    /** Hämtar om statusen från servern. */
    reload: () => Promise<void>;
    onError: (msg: string | null) => void;
    onSuccess: (msg: string) => void;
}

// Rad i statusrutan: liten rubrik med värdet under.
const StatusRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div>
        <span style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div style={{ fontWeight: 'bold', marginTop: '2px' }}>{value}</div>
    </div>
);

// Flik: anslutningsstatus och inställningar för myUplink.
const StatusTab: React.FC<Props> = ({ status, loading, timezone, reload, onError, onSuccess }) => {
    const [configForm, setConfigForm] = useState({
        client_id: '', client_secret: '', poll_interval_seconds: 60
    });
    const [configuring, setConfiguring] = useState(false);

    // Fyll formuläret med sparade värden när statusen hämtats
    useEffect(() => {
        if (status?.client_id) {
            setConfigForm(prev => ({
                ...prev,
                client_id: status.client_id!,
                poll_interval_seconds: status.poll_interval_seconds
            }));
        }
    }, [status]);

    const handleConfigure = async (e: React.FormEvent) => {
        e.preventDefault();
        setConfiguring(true);
        onError(null);
        try {
            await configureMyUplink({
                client_id: configForm.client_id,
                client_secret: configForm.client_secret,
                poll_interval_seconds: configForm.poll_interval_seconds,
                enabled: true
            });
            onSuccess('myUplink är inställt — hämtningen är igång');
            await reload();
        } catch (e: any) {
            onError(e.message);
        } finally {
            setConfiguring(false);
        }
    };

    const handleToggleEnabled = async () => {
        if (!status) return;
        try {
            await setMyUplinkEnabled(!status.enabled);
            await reload();
        } catch (e: any) {
            onError(e.message);
        }
    };

    const handlePollNow = async () => {
        try {
            await triggerMyUplinkPoll();
            onSuccess('Hämtning startad — värdena uppdateras om några sekunder');
            setTimeout(reload, 3000);
        } catch (e: any) {
            onError(e.message);
        }
    };

    return (
        <div>
            {/* Status */}
            <div style={{ ...ui.card, borderTop: `4px solid ${status?.enabled ? '#28a745' : '#6c757d'}` }}>
                <h3 style={{ marginTop: 0 }}>Anslutning</h3>
                {loading ? (
                    <p style={{ color: '#888' }}>Hämtar…</p>
                ) : status ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px 24px' }}>
                        <StatusRow label="Läge" value={
                            <span style={{ color: status.enabled ? '#28a745' : '#6c757d', fontWeight: 'bold' }}>
                                {status.enabled ? (status.running ? '● Igång' : '○ Påslaget (men inte igång)') : '○ Avstängt'}
                            </span>
                        } />
                        <StatusRow label="Client ID" value={status.client_id || '—'} />
                        <StatusRow label="Hämtar var" value={`${status.poll_interval_seconds} s`} />
                        <StatusRow label="Token" value={status.access_token || '—'} />
                        <StatusRow label="Senaste hämtning" value={status.last_poll_at ? formatInTZ(status.last_poll_at, timezone) : '—'} />
                        {status.last_error && (
                            <StatusRow label="Senaste fel" value={
                                <span style={{ color: '#dc3545' }}>{status.last_error}</span>
                            } />
                        )}
                    </div>
                ) : null}
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    {status?.client_id && (
                        <button onClick={handleToggleEnabled} style={ui.btn(status?.enabled ? '#6c757d' : '#28a745')}>
                            {status?.enabled ? 'Stäng av hämtning' : 'Slå på hämtning'}
                        </button>
                    )}
                    {status?.enabled && (
                        <button onClick={handlePollNow} style={ui.btn('#17a2b8')}>
                            ↻ Hämta nu
                        </button>
                    )}
                </div>
            </div>

            {/* Inställningar */}
            <div style={ui.card}>
                <h3 style={{ marginTop: 0 }}>
                    {status?.client_id ? 'Ändra inloggningsuppgifter' : 'Ställ in myUplink'}
                </h3>
                <p style={{ color: '#666', fontSize: '13px', marginTop: 0 }}>
                    Registrera din app på <a href="https://dev.myuplink.com" target="_blank" rel="noreferrer">dev.myuplink.com</a> för att få ett Client ID och en Client Secret. Välj typen <em>Client Credentials</em>.
                </p>
                <form onSubmit={handleConfigure}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div>
                            <label style={ui.label}>Client ID *</label>
                            <input style={ui.input} required value={configForm.client_id}
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                onChange={e => setConfigForm(p => ({ ...p, client_id: e.target.value }))} />
                        </div>
                        <div>
                            <label style={ui.label}>Client Secret *</label>
                            <input style={ui.input} required type="password" value={configForm.client_secret}
                                placeholder={status?.client_secret ? '(lämna tomt för att behålla den nuvarande)' : 'Skriv in din secret'}
                                onChange={e => setConfigForm(p => ({ ...p, client_secret: e.target.value }))} />
                        </div>
                        <div>
                            <label style={ui.label}>Hämta var (sekunder, minst 30)</label>
                            <input style={ui.input} type="number" min={30} max={3600}
                                value={configForm.poll_interval_seconds}
                                onChange={e => setConfigForm(p => ({ ...p, poll_interval_seconds: Number(e.target.value) }))} />
                        </div>
                    </div>
                    <button type="submit" disabled={configuring} style={ui.btn('#007bff')}>
                        {configuring ? 'Sparar…' : 'Spara och starta'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default StatusTab;
